<template>
  <AppCard as="article" class="card">
    <!-- The whole card is the way into its amendment, as an exchange's is (MOL-42, В-3, MOL-189): the
         removal lives in its sheet. No `aria-label`: it would silence the source, the amount and the
         note. The verb and the day are said first. Open without a connection too (С-19). -->
    <button class="body" type="button" @click="$emit('edit', income)">
      <span class="verb">{{ t('income.edit_on', { date: day }) }}</span>
      <OperationCardHead
        :day="head"
        :amended="income.amendedAt ? t('income.amended', { date: dayOf(income.amendedAt) }) : null"
      />
      <span class="line">
        <span class="source">{{ t(`income.source.${income.source}`) }}</span>
        <span class="amount">{{ amount }}</span>
      </span>
      <!-- Only when there is something to say: an income with neither is shorter. -->
      <span v-if="accountName || income.note" class="plate">
        <span v-if="accountName" class="account">
          <span class="account-label">{{ t('accounts.picker.row_income') }}</span>
          <span class="account-name">{{ t('accounts.card_name', { name: accountName }) }}</span>
        </span>
        <span v-if="income.note" class="note" :class="{ under: accountName }">
          <IconNote class="note-icon" aria-hidden="true" />
          <span class="note-text">{{ income.note }}</span>
        </span>
      </span>
    </button>
  </AppCard>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue'
import type { PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import IconNote from '~icons/mdi/note-text-outline'
import { formatMoney } from '@molvia/model'
import type { IncomeView } from '@molvia/model'
import AppCard from '@/components/AppCard.vue'
import OperationCardHead from '@/components/OperationCardHead.vue'
import { calendarDay, dayWords, purchaseDay } from '@/days'
import { useAccountsStore } from '@/stores/accounts'

/**
 * One income (MOL-81, handoff 03), in the grammar of an exchange's card: «when» on top, «from
 * where and how much» in the middle, the note on a plate below.
 */
export default defineComponent({
  name: 'IncomeCard',
  components: { AppCard, IconNote, OperationCardHead },
  props: {
    income: { type: Object as PropType<IncomeView>, required: true },
    /** The phone's today, held by the screen and asked again when it comes back (adversarial Н). */
    today: { type: String, required: true },
  },
  emits: {
    edit: (income: IncomeView) => typeof income === 'object',
  },
  setup(props) {
    const { t, locale } = useI18n()
    const dayOf = (when: Date): string => purchaseDay(when, locale.value, props.today)
    const accounts = useAccountsStore()
    return {
      t,
      dayOf,
      // «На счёт «Наличные ₽»», by the name the account has now (MOL-81 Р-6, MOL-123).
      accountName: computed(() =>
        props.income.accountId
          ? (accounts.accounts.find((account) => account.id === props.income.accountId)?.name ??
            null)
          : null,
      ),
      // A calendar day, never the moment of Yerevan's midnight: west of UTC+4 an income of
      // 1 September read «31 авг.» (adversarial Ж). «исправлен» is a moment, and stays one.
      day: computed(() =>
        calendarDay(props.income.receivedOn, locale.value, { day: 'numeric', month: 'short' }),
      ),
      // «Сегодня», «Вчера» by the phone's today (MOL-121, В-1); the button says the date.
      head: computed(() => dayWords(props.income.receivedOn, locale.value, props.today)),
      // As it was typed, as everywhere in «Деньги» (owner's decision В-1).
      amount: computed(() => formatMoney(props.income.amount, locale.value)),
    }
  },
})
</script>

<style scoped lang="scss">
.card {
  padding: 0;
}

.body {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  width: 100%;
  padding: var(--space-4);
  border: 0;
  border-radius: var(--radius-lg);
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: var(--surface-2);
  }

  &:focus-visible {
    @include focus-ring(-2px);
  }
}

.verb {
  @include visually-hidden;
}

.line {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-3);
}

.source {
  min-width: 0;
  font-size: var(--text-headline);
  font-weight: var(--weight-medium);
}

.amount {
  @include display-type;

  font-size: var(--text-card-figure);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.plate {
  display: grid;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius);
  background: var(--surface-2);
  color: var(--text-muted);
  font-size: var(--text-footnote);
}

.account {
  display: flex;
  justify-content: space-between;
  gap: var(--space-3);
}

.account-name {
  color: var(--text);
  font-weight: var(--weight-bold);
  overflow-wrap: anywhere;
  text-align: right;
}

.note {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);

  &.under {
    padding-top: var(--space-2);
    border-top: var(--hairline) solid var(--border);
  }
}

.note-icon {
  @include icon;

  font-size: var(--icon-sm);
}

.note-text {
  min-width: 0;
  overflow-wrap: anywhere;
}
</style>
